import { useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface LinkValue {
  url: string;
  label?: string;
}

interface LinkCellProps {
  value: LinkValue | null;
  onChange: (value: LinkValue | null) => void;
  readOnly?: boolean;
}

export function LinkCell({ value, onChange, readOnly }: LinkCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempUrl, setTempUrl] = useState(value?.url || '');
  const [tempLabel, setTempLabel] = useState(value?.label || '');

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSave = () => {
    if (!tempUrl.trim()) {
      onChange(null);
      setIsEditing(false);
      return;
    }

    let finalUrl = tempUrl.trim();
    if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      finalUrl = 'https://' + finalUrl;
    }

    if (isValidUrl(finalUrl)) {
      onChange({
        url: finalUrl,
        label: tempLabel.trim() || finalUrl,
      });
      setIsEditing(false);
    }
  };

  const handleClear = () => {
    setTempUrl('');
    setTempLabel('');
    onChange(null);
    setIsEditing(false);
  };

  if (readOnly && !value) {
    return <span className="text-gray-400 text-sm">-</span>;
  }

  if (readOnly && value) {
    return (
      <a
        href={value.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline"
      >
        <ExternalLink className="w-4 h-4" />
        <span className="truncate">{value.label || value.url}</span>
      </a>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-2 p-2 bg-white border rounded shadow-lg min-w-[300px]">
        <Input
          placeholder="URL (ex: google.com)"
          value={tempUrl}
          onChange={(e) => setTempUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') setIsEditing(false);
          }}
          autoFocus
        />
        <Input
          placeholder="Texto (opcional)"
          value={tempLabel}
          onChange={(e) => setTempLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') setIsEditing(false);
          }}
        />
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave}>
            Salvar
          </Button>
          <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
            Cancelar
          </Button>
          {value && (
            <Button size="sm" variant="ghost" onClick={handleClear}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!value) {
    return (
      <div
        className="text-gray-400 text-sm cursor-pointer hover:text-gray-600"
        onClick={() => setIsEditing(true)}
      >
        Adicionar link
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
      onClick={() => setIsEditing(true)}
    >
      <a
        href={value.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        <ExternalLink className="w-4 h-4" />
        <span className="truncate">{value.label || value.url}</span>
      </a>
    </div>
  );
}
